import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LivingMark } from '@ofself/shell';
import { markSoundStore } from '../lib/prefs.js';
import VoidWarp, { useVoidEgg } from '../components/VoidWarp.jsx';

// ABSORBER — ported from views/integrations/absorber.js (2026-08-24).
// File import + absorber agent: drop files, stream analysis, approve proposals.
// Four sub-views: portal (drop zone), review (the tabular mapping), session
// (chat + proposals), history. All absorber-* classes stay in integrations.css.
//
// The tabular helpers (parseDelimited, proposeMapping, rankSchemas,
// missingRequired, coerceCell, typeMismatches, buildNodes) stay in the legacy
// tabular.js classic script and are read off window. The acting identity comes
// through window.igIdentity() — the seam app.js opens for the two calls that
// cannot go through the api helper (multipart upload, SSE stream).

const api = {
  get: (p) => window.igApi.get(p),
  post: (p, b) => window.igApi.post(p, b),
};
const toast = (m) => window.showToast?.(m);
const identity = () => (window.igIdentity ? window.igIdentity() : {});
const groupHeaders = () => {
  const { activeGroupId } = identity();
  return activeGroupId ? { 'X-Group-Id': activeGroupId } : {};
};

const escHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// Simple markdown: bold, italic, code (assistant bubbles only).
function formatText(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br>');
}

// Wording for the layers we have a name for — NOT the set of layers; the
// response decides that.
const SCHEMA_GROUP_LABELS = {
  raw: 'raw signals',
  context: 'context entities',
  golden: 'identity',
  self: 'depth',
};

// KEYED BY THE ACTING IDENTITY, not just the person — a session resumed under
// the wrong identity is an error nothing on the page explains, and the two
// contexts keep separate histories anyway.
function historyKey() {
  const { userId, activeGroupId } = identity();
  return `sources_sessions:${activeGroupId ? `GROUP#${activeGroupId}` : `USER#${userId || 'default'}`}`;
}
function getHistory() {
  try { return JSON.parse(localStorage.getItem(historyKey()) || '[]'); } catch { return []; }
}

// Only for files we can actually read here — a PDF has no columns to map.
const TABULAR = /\.(csv|tsv|txt)$/i;

// A node with no title is a row of anonymous JSON in a list.
function pickTitleColumn(header, mapping) {
  const mapped = header.filter((_, i) => mapping[i]);
  const named = mapped.find((c) => /name|title|subject|number|who|from/i.test(c));
  return named || mapped[0] || null;
}

export default function Absorber({ active }) {
  const [sub, setSub] = useState('portal'); // portal | review | session | history
  const [files, setFiles] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [messages, setMessages] = useState([]); // { id, role, html }
  const [streamText, setStreamText] = useState(null); // assistant bubble mid-stream
  const [phaseText, setPhaseText] = useState(null);
  const [preview, setPreview] = useState(null); // proposal id open in the data pane
  const [pane, setPane] = useState('chat');
  const [review, setReview] = useState(null); // { file, header, rows, rawSample }
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0); // 0→1, the mark's absorb

  // Session identity + stream bookkeeping — refs, because the SSE loop reads
  // them mid-flight and must never see a stale closure.
  const sess = useRef({ conversationId: null, artifactIds: [], rawFileNodeIds: [], startedAt: null, pendingFile: null });
  const streaming = useRef(false);
  const phase = useRef('idle');
  const filesRef = useRef(files); filesRef.current = files;
  const proposalsRef = useRef(proposals); proposalsRef.current = proposals;
  const msgId = useRef(0);
  const messagesEl = useRef(null);

  const saveToHistory = useCallback(() => {
    const s = sess.current;
    if (!s.conversationId) return;
    const all = getHistory();
    const entry = {
      id: s.conversationId,
      files: filesRef.current.map((f) => f.name),
      proposal_count: proposalsRef.current.length,
      started_at: s.startedAt || new Date().toISOString(),
    };
    const idx = all.findIndex((x) => x.id === entry.id);
    if (idx >= 0) all[idx] = entry; else all.unshift(entry);
    localStorage.setItem(historyKey(), JSON.stringify(all.slice(0, 20)));
  }, []);

  const scrollMessages = () => {
    const el = messagesEl.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  useEffect(scrollMessages, [messages, streamText]);

  const appendMessage = (role, text) => {
    setMessages((m) => [...m, {
      id: ++msgId.current, role,
      html: role === 'user' ? escHtml(text) : formatText(text),
    }]);
  };

  // ── Schema groups — one fetch per view life, warmed on mount ─────────────
  const schemaGroups = useRef(null);
  const loadSchemaGroups = useCallback(() => {
    if (!schemaGroups.current) {
      schemaGroups.current = api.get('/process/schemas')
        .then((r) => {
          const groups = {};
          for (const [layer, list] of Object.entries(r?.groups || {})) {
            if (!Array.isArray(list) || !list.length) continue;
            groups[SCHEMA_GROUP_LABELS[layer] || layer] = list;
          }
          return Object.keys(groups).length ? groups : null;
        })
        .catch(() => null);
    }
    return schemaGroups.current;
  }, []);
  useEffect(() => { if (active) loadSchemaGroups(); }, [active, loadSchemaGroups]);

  // ── SSE stream ───────────────────────────────────────────────────────────
  const advancePhase = (p) => {
    const phases = ['reading', 'analyzing', 'creating', 'responding'];
    if (phases.indexOf(p) > phases.indexOf(phase.current)) {
      phase.current = p;
      const labels = {
        reading: 'reading your file...', analyzing: 'analyzing...',
        creating: 'creating proposals...', responding: 'writing response...',
      };
      setPhaseText(labels[p] || p);
    }
  };

  const startStream = useCallback(async (opts) => {
    streaming.current = true;
    phase.current = 'reading';
    setPhaseText('reading your file...');
    setStreamText(null);
    let text = '';
    let hasBubble = false;
    const ensureBubble = () => { hasBubble = true; };

    try {
      const res = await fetch('/sources/stream', {
        method: 'POST',
        // Raw, not through `api`: an SSE response is read off the body stream.
        // Same X-Group-Id, same reason as the upload.
        headers: { 'Content-Type': 'application/json', ...groupHeaders() },
        body: JSON.stringify({
          conversation_id: sess.current.conversationId,
          message: opts.message || 'Analyze this file and extract identity graph nodes.',
          artifact_ids: sess.current.artifactIds,
          raw_file_node_ids: sess.current.rawFileNodeIds,
          filename: filesRef.current.map((f) => f.name).join(', '),
          target_schemas: opts.target_schemas || [],
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Stream failed');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('event:')) { currentEvent = line.slice(6).trim(); continue; }
          if (!line.startsWith('data:')) continue;
          const raw = line.slice(5).trim();
          if (raw === '[DONE]') continue;
          try {
            const data = JSON.parse(raw);
            const type = data.type || currentEvent;

            if (type === 'proposal' || type === 'proposal_updated') {
              advancePhase('creating');
              const p = data.data || data;
              if (!p.status) p.status = 'PENDING';
              setProposals((ps) => {
                if (type === 'proposal') return [...ps, p];
                const idx = ps.findIndex((x) => x.id === p.id);
                if (idx < 0) return ps;
                const next = ps.slice();
                next[idx] = { ...next[idx], ...p };
                return next;
              });
              saveToHistory();
            } else if (type === 'content') {
              advancePhase('responding');
              ensureBubble();
              text += data.text || '';
              setStreamText(text);
            } else if (type === 'paradigm_write' || type === 'tool_call' || type === 'tool_use') {
              advancePhase('analyzing');
            } else if (type === 'done' || type === 'end' || type === 'message_stop') {
              if (data.conversation_id) sess.current.conversationId = data.conversation_id;
            } else if (type === 'error') {
              ensureBubble();
              text = '';
              setStreamText(null);
              appendMessage('assistant', `error: ${data.message || 'unknown'}`);
            }
          } catch (_) {}
        }
      }
    } catch (err) {
      appendMessage('assistant', `error: ${err.message}`);
    }

    streaming.current = false;
    phase.current = 'done';
    setPhaseText(null);
    if (hasBubble && text) {
      setStreamText(null);
      setMessages((m) => [...m, { id: ++msgId.current, role: 'assistant', html: formatText(text) }]);
    }
    saveToHistory();
  }, [saveToHistory]);

  // ── Handing the file to the absorber — straight to reading, unconstrained ─
  const handToAbsorber = useCallback(() => {
    setReview(null);
    setSub('session');
    startStream({
      message: 'Analyze this file and extract identity graph nodes.',
      target_schemas: [],
    });
  }, [startStream]);

  // ── Tabular files go through the mapping review first ────────────────────
  const showProcessDialog = useCallback(async () => {
    const file = sess.current.pendingFile || null;
    if (file && TABULAR.test(file.name || '') && typeof window.parseDelimited === 'function') {
      const text = await file.slice(0, 64 * 1024).text().catch(() => '');
      const { header, rows } = window.parseDelimited(text, 200);
      // Two columns and a row is a table; one column is prose with commas in it.
      if (header.length >= 2 && rows.length) {
        const rawSample = text.split('\n').slice(0, 40).join('\n');
        setReview({ file, header, rows, rawSample });
        setSub('review');
        return;
      }
    }
    handToAbsorber();
  }, [handToAbsorber]);

  // ── File upload ──────────────────────────────────────────────────────────
  // XHR, not fetch: the upload's own progress IS the mark's absorb — the
  // figure falls into its centre as the bytes go — and fetch cannot report it.
  // Multipart still lets the browser set its own boundary; X-Group-Id is the
  // only thing naming which graph this is for.
  const postFile = (formData, onProgress) => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/sources/upload');
    for (const [k, v] of Object.entries(groupHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded / e.total); };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || '{}'); } catch (_) {}
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else reject(new Error(body.error || 'Upload failed'));
    };
    xhr.onerror = () => reject(new Error('network error'));
    xhr.send(formData);
  });

  const handleUpload = useCallback(async (file) => {
    setUploading(true);
    setUploadProgress(0.04);
    // A small file sends its bytes before the first progress event, and the
    // server's reply is most of the wait — so the figure is also drawn in on
    // a clock, easing toward 80%, and real progress wins whenever it is ahead.
    const drift = setInterval(() => setUploadProgress((p) => (p < 0.8 ? p + (0.8 - p) * 0.06 : p)), 90);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('force_new', sess.current.conversationId ? 'false' : 'true');
      if (sess.current.conversationId) formData.append('conversation_id', sess.current.conversationId);

      // the bytes carry the figure most of the way in; the server's answer
      // (the artifact, the file's node) closes it
      const data = await postFile(formData, (f) => setUploadProgress((p) => Math.max(p, 0.04 + 0.76 * f)));
      clearInterval(drift);
      setUploadProgress(1);
      // let the collapse land before the session takes the page
      await new Promise((r) => setTimeout(r, 520));
      sess.current.conversationId = data.conversation_id;
      sess.current.artifactIds = [...sess.current.artifactIds, data.artifact_id];
      // The file's own graph node: what every node absorbed from it links back to.
      if (data.raw_file_node_id) sess.current.rawFileNodeIds = [...sess.current.rawFileNodeIds, data.raw_file_node_id];
      sess.current.startedAt = sess.current.startedAt || new Date().toISOString();
      // The File itself, not just its name — the mapping review reads the
      // first 64KB in the browser.
      sess.current.pendingFile = file;
      setFiles((f) => [...f, { name: data.filename || file.name, artifact_id: data.artifact_id, size: file.size }]);
      setUploading(false);
      setUploadProgress(0);
      setSub('session');
      // Session paints first; the review (or the stream) follows.
      setTimeout(() => { saveToHistory(); showProcessDialog(); }, 0);
    } catch (err) {
      clearInterval(drift);
      // the figure grows back out: nothing was absorbed
      setUploading(false);
      setUploadProgress(0);
      toast('Upload failed: ' + err.message);
    }
  }, [saveToHistory, showProcessDialog]);

  // ── Proposal actions ─────────────────────────────────────────────────────
  const setStatus = (pid, status) => setProposals((ps) => ps.map((p) => (p.id === pid ? { ...p, status } : p)));
  const pending = proposals.filter((p) => p.status === 'PENDING' || !p.status);

  const approve = async (p, btn) => {
    btn.disabled = true; btn.textContent = '...';
    try {
      await api.post(`/sources/proposals/${p.id}/approve`);
      setStatus(p.id, 'APPLIED');
    } catch (err) {
      btn.textContent = 'approve'; btn.disabled = false;
      toast('Error: ' + err.message);
    }
  };
  const reject = async (p, btn) => {
    btn.disabled = true; btn.textContent = '...';
    try {
      await api.post(`/sources/proposals/${p.id}/reject`);
      setStatus(p.id, 'REJECTED');
    } catch (err) {
      btn.textContent = 'reject'; btn.disabled = false;
      toast('Error: ' + err.message);
    }
  };
  const approveAll = async (btn) => {
    if (!pending.length) return;
    btn.disabled = true; btn.textContent = 'approving...';
    try {
      await api.post('/sources/proposals/approve-all', { proposal_ids: pending.map((p) => p.id) });
      const ids = new Set(pending.map((p) => p.id));
      setProposals((ps) => ps.map((p) => (ids.has(p.id) ? { ...p, status: 'APPLIED' } : p)));
      toast(`${pending.length} proposal${pending.length !== 1 ? 's' : ''} approved`);
    } catch (err) {
      toast('Error: ' + err.message);
    }
    btn.disabled = false;
  };

  // The pane follows the list rather than outliving it: a previewed proposal
  // that leaves the list closes the pane.
  useEffect(() => {
    if (preview && !proposals.some((p) => p.id === preview)) {
      setPreview(null); setPane('chat');
    }
  }, [proposals, preview]);

  const openPreview = (pid) => { setPreview(pid); setPane('preview'); };
  const closePreview = () => { setPreview(null); setPane('chat'); };

  const resetToPortal = () => {
    sess.current = { conversationId: null, artifactIds: [], rawFileNodeIds: [], startedAt: null, pendingFile: null };
    setFiles([]); setProposals([]); setMessages([]); setStreamText(null);
    setPreview(null); setPane('chat'); setReview(null);
    setSub('portal');
  };

  if (!active) return null;

  if (sub === 'review' && review) {
    return (
      <MappingReview
        {...review}
        loadSchemaGroups={loadSchemaGroups}
        onCancel={() => { setReview(null); setSub('portal'); }}
        onHandToAbsorber={handToAbsorber}
        onDone={resetToPortal}
      />
    );
  }

  if (sub === 'history') {
    return (
      <HistoryList
        onBack={() => setSub('portal')}
        onOpen={(session) => {
          sess.current.conversationId = session.id;
          sess.current.startedAt = session.started_at;
          sess.current.pendingFile = null;
          setFiles((session.files || []).map((f) => ({ name: f })));
          setProposals([]);
          setMessages([]);
          setSub('session');
        }}
      />
    );
  }

  if (sub === 'session') {
    return (
      <SessionView
        files={files}
        proposals={proposals}
        pending={pending}
        messages={messages}
        streamText={streamText}
        phaseText={phaseText}
        preview={preview}
        pane={pane}
        messagesEl={messagesEl}
        onBack={() => { setPreview(null); setPane('chat'); setSub('portal'); }}
        onAddFile={handleUpload}
        onSend={(text) => {
          if (!text || streaming.current) return false;
          appendMessage('user', text);
          startStream({ message: text });
          return true;
        }}
        onApprove={approve}
        onReject={reject}
        onApproveAll={approveAll}
        onOpenPreview={openPreview}
        onClosePreview={closePreview}
        onShowPane={setPane}
      />
    );
  }

  return <Portal uploading={uploading} progress={uploadProgress} onUpload={handleUpload} onHistory={() => setSub('history')} />;
}

// ── Portal sub-view (drop zone) ────────────────────────────────────────────
// THE PAGE IS THE MARK (owner call 2026-09-25): nothing on the slate but the
// convergence figure, alive — it breathes, rings where the pointer brushes
// it, WAKES while a file is dragged over the window, and ABSORBS the file as
// it uploads, falling into its own centre. A press on the figure opens the
// file picker, so dragging is never the only way in.
// Drag handlers bind to the DOCUMENT — a near-miss drop anywhere the page
// hasn't claimed navigates away and destroys the session — and unbind on
// unmount (React's cleanup replaces the legacy alive() hack).
// the mark's resting turn, rad/s (about one turn every two and a half
// minutes): the figure and the motes at its string tips share it
const MARK_DRIFT = 0.045;

function Portal({ uploading, progress, onUpload, onHistory }) {
  const [dragover, setDragover] = useState(false);
  const fileInput = useRef(null);
  const historyCount = getHistory().length;
  const sound = markSoundStore.useStore();
  // the desk is the cloth the mark sits in (VoidWarp.jsx): both are handed
  // the SAME state, so the fabric turns, falls and breathes with the figure.
  // "void" typed on the page lends the mark an absorb of its own.
  const desk = useRef(null);
  const egg = useVoidEgg(!uploading);
  const markState = uploading || egg !== null ? 'absorb' : dragover ? 'wake' : 'idle';
  const markProgress = uploading ? progress : egg ?? 0;
  const holeCenter = useCallback(() => {
    const r = desk.current?.querySelector('.ofself-living-mark')?.getBoundingClientRect();
    return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
  }, []);

  useEffect(() => {
    let dragDepth = 0;
    const isFileDrag = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onDragEnter = (e) => {
      if (!isFileDrag(e)) return;
      e.preventDefault();
      if (++dragDepth === 1) setDragover(true);
    };
    // Without preventDefault on dragover the drop event never fires at all.
    const onDragOver = (e) => { if (isFileDrag(e)) e.preventDefault(); };
    const onDragLeave = (e) => {
      if (!isFileDrag(e)) return;
      if (--dragDepth <= 0) { dragDepth = 0; setDragover(false); }
    };
    const onDrop = (e) => {
      e.preventDefault(); // unconditional: stop the browser navigating
      dragDepth = 0;
      setDragover(false);
      if (!isFileDrag(e)) return;
      const dropped = Array.from(e.dataTransfer.files || []);
      if (!dropped.length) return;
      // One file per upload is the endpoint's contract; say so rather than
      // dropping the rest on the floor.
      if (dropped.length > 1) toast(`taking ${dropped[0].name} — drop one file at a time`);
      onUpload(dropped[0]);
    };
    document.addEventListener('dragenter', onDragEnter);
    document.addEventListener('dragover', onDragOver);
    document.addEventListener('dragleave', onDragLeave);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragenter', onDragEnter);
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('dragleave', onDragLeave);
      document.removeEventListener('drop', onDrop);
    };
  }, [onUpload]);

  return (
    // the absorber is where the person composes (a file in, nodes out), so it
    // wears the desk's dot grid, though the rest of #main-area stays plain
    <div className="absorber-wrap ofself-desk is-draining" ref={desk}>
      <VoidWarp host={desk} center={holeCenter} state={markState} progress={markProgress} drift={MARK_DRIFT} scale={0.62} />
      <div className="absorber-portal absorber-portal--living" id="absorber-drop">
        {historyCount ? (
          <button type="button" className="absorber-history-link" onClick={onHistory}>
            past sessions ({historyCount}) →
          </button>
        ) : null}
        <LivingMark
          app="integrations"
          state={markState}
          progress={markProgress}
          drift={MARK_DRIFT}
          brush={false}
          sound={sound}
          onPress={uploading ? undefined : () => fileInput.current?.click()}
          label={uploading ? 'Absorbing your file' : 'Attach a file, or drop one anywhere'}
          scale={0.62}
        />
        <button type="button" className="ofself-btn absorber-choose-btn absorber-choose-btn--float"
                disabled={uploading} onClick={() => fileInput.current?.click()}>
          {/* a solid disc with the plus CUT OUT (evenodd), so the plus is true
              negative space: white on the light ground, the ground's own dark
              at night — a painted white plus would vanish on the night disc */}
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" fillRule="evenodd"
                  d="M12 2a10 10 0 1 0 0 20a10 10 0 1 0 0-20zM11 7h2v4h4v2h-4v4h-2v-4H7v-2h4z" />
          </svg>
          attach
        </button>
        <input type="file" ref={fileInput} style={{ display: 'none' }}
               onChange={(e) => { if (e.target.files[0]) onUpload(e.target.files[0]); }} />
      </div>
    </div>
  );
}

// ── Session sub-view (chat + proposals) ────────────────────────────────────
function SessionView({
  files, proposals, pending, messages, streamText, phaseText, preview, pane,
  messagesEl, onBack, onAddFile, onSend, onApprove, onReject, onApproveAll,
  onOpenPreview, onClosePreview, onShowPane,
}) {
  const addInput = useRef(null);
  const [input, setInput] = useState('');
  const previewProposal = preview ? proposals.find((p) => p.id === preview) : null;

  const send = () => {
    const text = input.trim();
    if (onSend(text)) setInput('');
  };

  return (
    <div className="absorber-wrap">
      <div className="absorber-session-header">
        <button type="button" className="absorber-back-btn" onClick={onBack}>← absorber</button>
        <div className="absorber-file-chips">
          {files.map((f, i) => <span className="absorber-file-chip" key={i}>{f.name}</span>)}
        </div>
        <button type="button" className="surface-action-btn muted absorber-add-file-btn"
                onClick={() => addInput.current?.click()}>+ file</button>
        <input type="file" ref={addInput} style={{ display: 'none' }}
               onChange={(e) => { if (e.target.files[0]) onAddFile(e.target.files[0]); }} />
      </div>
      <div className="absorber-session-split">
        <div className="absorber-chat-panel">
          {/* The tab strip only exists once something is open in it. */}
          <div className="absorber-panel-tabs" hidden={!previewProposal}>
            <button type="button" className={`absorber-panel-tab${pane === 'chat' ? ' is-active' : ''}`}
                    onClick={() => onShowPane('chat')}>chat</button>
            <button type="button" className={`absorber-panel-tab${pane === 'preview' ? ' is-active' : ''}`}
                    onClick={() => onShowPane('preview')}>data</button>
            <button type="button" className="absorber-panel-tab-close" title="close the data tab"
                    aria-label="close the data tab" onClick={onClosePreview}>×</button>
          </div>
          {/* BOTH panes stay mounted and are toggled with [hidden] — same law
              as the legacy view, kept so the chat never loses its scroll. */}
          <div className="absorber-pane" id="absorber-chat-pane" hidden={pane !== 'chat'}>
            <div className="absorber-messages" ref={messagesEl}>
              {messages.map((m) => (
                <div className={`absorber-msg ${m.role}`} key={m.id}>
                  <div className="absorber-bubble" dangerouslySetInnerHTML={{ __html: m.html }} />
                </div>
              ))}
              {streamText != null ? (
                <div className="absorber-msg assistant">
                  <div className="absorber-bubble streaming"
                       dangerouslySetInnerHTML={{ __html: formatText(streamText) }} />
                </div>
              ) : null}
            </div>
            <div className="absorber-phase" style={{ display: phaseText ? '' : 'none' }}>
              <span className="absorber-phase-dot" />
              <span className="absorber-phase-text">{phaseText || ''}</span>
            </div>
            <div className="absorber-input-wrap">
              <input className="absorber-input" placeholder="ask about your data..." autoComplete="off"
                     value={input} onChange={(e) => setInput(e.target.value)}
                     onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <button type="button" className="surface-action-btn muted absorber-send-btn" onClick={send}>send</button>
            </div>
          </div>
          <div className="absorber-pane absorber-preview-pane" hidden={pane !== 'preview'}>
            {previewProposal ? <PreviewPane p={previewProposal} /> : null}
          </div>
        </div>
        <div className="absorber-proposals-panel">
          <div className="absorber-proposals-header">
            <span className="absorber-proposals-label">proposals</span>
            <button type="button" className="surface-action-btn muted absorber-approve-all"
                    style={pending.length ? undefined : { display: 'none' }}
                    onClick={(e) => onApproveAll(e.target)}>approve all ({pending.length})</button>
          </div>
          <div className="absorber-proposals-list">
            {proposals.length
              ? proposals.map((p) => (
                <ProposalCard p={p} key={p.id} previewing={preview === p.id}
                              onApprove={onApprove} onReject={onReject} onOpenPreview={onOpenPreview} />
              ))
              : <div className="absorber-empty">nodes extracted from your file will appear here</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Proposal cards ─────────────────────────────────────────────────────────
function ProposalCard({ p, previewing, onApprove, onReject, onOpenPreview }) {
  const sLabel = (p.schema_type || '').replace(/^(raw|context|golden|self):/, '');
  const statusClass = (p.status || 'PENDING').toLowerCase();
  const isPending = !p.status || p.status === 'PENDING';
  const entities = p.canonical_data?.entities || [];
  // Shown whenever there IS data behind the card: approving without reading
  // should be a choice, not the only option.
  const hasData = entities.length > 0 || !!(p.canonical_data && p.canonical_data.updates);

  return (
    <div className={`absorber-proposal-card ${statusClass}${previewing ? ' is-previewing' : ''}`}>
      <div className="absorber-proposal-top">
        <span className="absorber-proposal-schema">{sLabel}</span>
        <span className="absorber-proposal-title">{p.title || ''}</span>
        {p.type === 'EDIT_NODE' ? <span className="absorber-proposal-edit-badge">edit</span> : null}
      </div>
      {p.description ? <div className="absorber-proposal-desc">{p.description}</div> : null}
      {entities.length ? (
        <div className="absorber-proposal-entities">
          {entities.slice(0, 3).map((e, i) => (
            <div className="absorber-proposal-entity" key={i}>{e.title || ''}</div>
          ))}
          {/* The overflow line names what you cannot see, so it opens the data. */}
          {entities.length > 3 ? (
            <button type="button" className="absorber-proposal-entity absorber-proposal-more"
                    onClick={() => onOpenPreview(p.id)}>+{entities.length - 3} more</button>
          ) : null}
        </div>
      ) : null}
      <div className="absorber-proposal-actions">
        {hasData ? (
          <button type="button" className="surface-action-btn muted absorber-proposal-preview"
                  onClick={() => onOpenPreview(p.id)}>preview data</button>
        ) : null}
        {isPending ? (
          <>
            <button type="button" className="surface-action-btn muted absorber-proposal-approve"
                    onClick={(e) => onApprove(p, e.target)}>approve</button>
            <button type="button" className="surface-action-btn muted absorber-proposal-reject"
                    onClick={(e) => onReject(p, e.target)}>reject</button>
          </>
        ) : <span className="absorber-proposal-status">{(p.status || '').toLowerCase()}</span>}
      </div>
    </div>
  );
}

// ── Data preview — what a proposal would actually write, in full ───────────
// Deliberately a PANE and not a modal: reading rows against what the agent
// said about them means having both.
function PreviewValue({ v }) {
  if (v === null || v === undefined) return <span className="absorber-preview-null">—</span>;
  if (typeof v === 'boolean' || typeof v === 'number') return <span className="absorber-preview-scalar">{String(v)}</span>;
  if (typeof v === 'string') {
    return v.trim() ? <>{v}</> : <span className="absorber-preview-null">empty</span>;
  }
  // Objects and arrays as JSON — the reader approves the shape, not a summary.
  return <pre className="absorber-preview-json">{JSON.stringify(v, null, 2)}</pre>;
}

function PreviewFields({ valueJson }) {
  const keys = Object.keys(valueJson || {});
  if (!keys.length) return <div className="absorber-preview-empty">no fields</div>;
  return (
    <dl className="absorber-preview-fields">
      {keys.map((k) => (
        <React.Fragment key={k}>
          <dt className="absorber-preview-key">{k}</dt>
          <dd className="absorber-preview-val"><PreviewValue v={valueJson[k]} /></dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

function PreviewPane({ p }) {
  const cd = p.canonical_data || {};
  const entities = cd.entities || [];
  const sLabel = (p.schema_type || '').replace(/^(raw|context|golden|self):/, '');
  const paneEl = useRef(null);
  // Always from the top: a pane still scrolled to the last proposal's tenth
  // row reads as the wrong data.
  useEffect(() => { if (paneEl.current?.parentElement) paneEl.current.parentElement.scrollTop = 0; }, [p.id]);

  let body;
  if (entities.length) {
    // Numbered, because the card said "+6 more" and the first question is
    // which six.
    body = entities.map((e, i) => (
      <section className="absorber-preview-entity" key={i}>
        <header className="absorber-preview-entity-top">
          <span className="absorber-preview-idx">{i + 1}</span>
          <span className="absorber-preview-entity-title">{e.title || '(untitled)'}</span>
          {e.schema_name ? <span className="absorber-preview-schema">{e.schema_name}</span> : null}
        </header>
        <PreviewFields valueJson={e.value_json} />
      </section>
    ));
  } else if (cd.updates) {
    // An edit names the node it changes.
    body = (
      <section className="absorber-preview-entity">
        <header className="absorber-preview-entity-top">
          <span className="absorber-preview-entity-title">edits to an existing node</span>
          {cd.node_id ? <span className="absorber-preview-schema">{cd.node_id}</span> : null}
        </header>
        <PreviewFields valueJson={cd.updates} />
      </section>
    );
  } else {
    body = <div className="absorber-preview-empty">this proposal carries no structured data</div>;
  }

  return (
    <div ref={paneEl}>
      <div className="absorber-preview-head">
        <div className="absorber-preview-headline">
          {sLabel ? <span className="absorber-proposal-schema">{sLabel}</span> : null}
          <span className="absorber-preview-title">{p.title || ''}</span>
        </div>
        {p.description ? <p className="absorber-preview-desc">{p.description}</p> : null}
        <div className="absorber-preview-count">
          {entities.length ? `${entities.length} ${entities.length === 1 ? 'node' : 'nodes'} would be written` : ''}
        </div>
      </div>
      <div className="absorber-preview-body">{body}</div>
    </div>
  );
}

// ── The mapping review ─────────────────────────────────────────────────────
// A thousand rows of one shape are one decision made four ways; those four get
// checked against real rows, before a single node exists.
function MappingReview({ file, header, rows, rawSample, loadSchemaGroups, onCancel, onHandToAbsorber, onDone }) {
  const [schemaName, setSchemaName] = useState('');
  const [allSchemas, setAllSchemas] = useState([]);
  const [fields, setFields] = useState([]);
  const [mapping, setMapping] = useState(() => header.map(() => null));
  const [proposedBy, setProposedBy] = useState('matching'); // matching | absorber
  const [agentReason, setAgentReason] = useState('');
  const [ranked, setRanked] = useState(null);
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const titleColumn = pickTitleColumn(header, mapping);
  const typeOf = (name) => (fields.find((f) => f.name === name) || {}).type || 'string';

  const loadFields = useCallback(async (name, agentMapping) => {
    if (!name) { setFields([]); setMapping(header.map(() => null)); return; }
    let fs = [];
    try {
      const r = await api.get(`/process/schemas/${encodeURIComponent(name)}/fields`);
      fs = r.fields || [];
    } catch (_) { /* drawn as "could not read the shape" below */ }
    if (!alive.current) return;
    setFields(fs);
    if (agentMapping) {
      // The agent answers in column names; the table works in positions.
      // Anything it names that is not a real field is dropped.
      const valid = new Set(fs.map((f) => f.name));
      setMapping(header.map((col) => {
        const f = agentMapping[col];
        return f && valid.has(f) ? f : null;
      }));
    } else {
      setMapping(window.proposeMapping(header, fs));
    }
  }, [header]);

  // Ask first, draw second: the matcher's answer shows immediately, replaced
  // the moment the agent answers.
  useEffect(() => {
    let live = true;
    (async () => {
      const groups = await loadSchemaGroups();
      let withFields = [];
      let rankedLocal = null;
      try {
        const r = await api.get('/process/schema-fields');
        withFields = (r.schemas || []).map((sc) => sc.schema_name);
        rankedLocal = window.rankSchemas(header, r.schemas || []);
      } catch (_) { /* no preselection */ }
      if (!live) return;
      // The union, not the grouped list alone — both halves of this screen
      // choose from the same set.
      const all = [...new Set([...withFields, ...Object.values(groups || {}).flat()])].sort();
      setAllSchemas(all);
      setRanked(rankedLocal);
      // Nothing convincing means nothing selected.
      const initial = rankedLocal ? rankedLocal.schema_name : '';
      setSchemaName(initial);
      await loadFields(initial);
      // Whose proposal is on screen: the agent's is asked for, the matcher is
      // what happens when it cannot answer.
      try {
        const r = await api.post('/process/tabular/propose', {
          filename: file.name, sample: rawSample, schemas: all,
        });
        const p = r && r.proposal;
        if (!p || !live) return;
        let name = initial;
        if (p.schema_name && all.includes(p.schema_name)) name = p.schema_name;
        setSchemaName(name);
        setProposedBy('absorber');
        setAgentReason(p.reason || '');
        await loadFields(name, p.mapping || {});
      } catch (_) { /* the matcher's answer stands */ }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── What a cell actually becomes — the conversion is the answer ──────────
  const cellBecomes = (raw, field) => {
    const text = String(raw ?? '').trim();
    if (!field) {
      return text
        ? { klass: 'absorber-map-dropped', node: text }
        : { klass: 'absorber-map-quiet', node: 'not used' };
    }
    if (text === '') return { klass: 'absorber-map-quiet', node: 'empty — field left out' };
    const type = typeOf(field);
    const { ok, value, from } = window.coerceCell(text, type, field);
    if (!ok) {
      return { klass: 'absorber-map-bad',
        node: <>{text} <span className="absorber-map-because">not {type} — rejected</span></> };
    }
    const shown = typeof value === 'string' ? value : JSON.stringify(value);
    return { klass: '', node: from
      ? <>{shown} <span className="absorber-map-from">← {from}</span></>
      : shown };
  };

  const missing = fields.length ? window.missingRequired(mapping, fields) : [];
  const sample = fields.length ? window.buildNodes(header, rows.slice(0, 3), mapping, schemaName, titleColumn, fields) : [];
  const badTypes = fields.length ? window.typeMismatches(header, rows, mapping, fields) : [];
  const unmapped = header.filter((_, i) => !mapping[i]);
  const rowsLabel = `${rows.length}${rows.length >= 200 ? '+' : ''}`;

  const changeField = (i, chosen) => {
    setMapping((m) => {
      // One field, one column: taking a field releases it from wherever it was.
      let next = chosen ? m.map((x) => (x === chosen ? null : x)) : m.slice();
      next[i] = chosen || null;
      return next;
    });
  };

  const write = async () => {
    setWriting(true);
    setStatus('reading the whole file…');
    // The sample came from the first 64KB; the run needs all of it.
    const full = window.parseDelimited(await file.text(), 1e9);
    const nodes = window.buildNodes(full.header, full.rows, mapping, schemaName, titleColumn, fields);

    // Batched — one request per row is 1,204 requests and one request of 1,204
    // rows is a timeout. `skip` on duplicate so a re-run resumes.
    const SIZE = 200;
    let created = 0, skipped = 0, failed = 0;
    for (let i = 0; i < nodes.length; i += SIZE) {
      setStatus(`writing ${i + 1}–${Math.min(i + SIZE, nodes.length)} of ${nodes.length}…`);
      try {
        const r = await api.post('/api/nodes/batch', {
          nodes: nodes.slice(i, i + SIZE), on_duplicate: 'skip',
        });
        created += (r.created || r.results?.created || []).length || r.created_count || 0;
        skipped += (r.skipped || []).length || 0;
        failed += (r.errors || []).length || 0;
      } catch (e) {
        failed += Math.min(SIZE, nodes.length - i);
      }
    }
    if (!alive.current) return;
    setStatus(`${created} written${skipped ? `, ${skipped} already there` : ''}${failed ? `, ${failed} rejected` : ''}`);
    setWriting(false);
    if (!failed) setTimeout(() => { if (alive.current) onDone(); }, 1200);
  };

  return (
    <div className="absorber-map-page">
      <div className="absorber-map-head">
        <button type="button" className="absorber-back-btn" onClick={onCancel}>← absorber</button>
      </div>
      <div className="absorber-process-title">check what this becomes</div>
      <div className="absorber-process-file">{file.name} · {rowsLabel} rows</div>
      <div className="absorber-map-by">
        {proposedBy === 'absorber'
          ? <>the absorber read it{agentReason ? ` — ${agentReason}` : ''}</>
          : schemaName
            ? <>matched {ranked ? ranked.hits : ''} of these columns to <span className="mono">{schemaName}</span>&#39;s fields — the absorber is still reading</>
            : 'no schema fits these columns closely — pick one, or let the absorber read it'}
      </div>

      <div className="absorber-map-lede">
        Each of these {rowsLabel} rows becomes one node. Check the columns landed in the right
        fields — the three samples below are literally what gets written.
      </div>

      <div className="absorber-process-field absorber-map-schema-row">
        <label className="automation-field-label">each row becomes a</label>
        <select className="auto-form-select" value={schemaName}
                onChange={(e) => { setSchemaName(e.target.value); loadFields(e.target.value); }}>
          <option value="">— pick what these rows are</option>
          {allSchemas.map((n) => <option value={n} key={n}>{n}</option>)}
        </select>
        {/* "Is this a plain table at all?" is worth asking before someone reads
            five mappings and three sample rows. */}
        <button type="button" className="surface-action-btn muted" onClick={onHandToAbsorber}>
          or have the absorber read it
        </button>
      </div>

      {fields.length ? (
        <>
          <table className="absorber-map-table">
            <tbody>
              <tr><th>column</th><th></th><th>field</th><th>type</th><th>first row becomes</th></tr>
              {header.map((col, i) => {
                const becomes = cellBecomes(rows[0] && rows[0][i], mapping[i]);
                const f = mapping[i] ? (fields.find((x) => x.name === mapping[i]) || {}) : null;
                return (
                  <tr key={i}>
                    <td className="mono">{col}</td>
                    <td className="absorber-map-arrow">→</td>
                    <td>
                      <select className="absorber-map-field" value={mapping[i] || ''}
                              onChange={(e) => changeField(i, e.target.value)}>
                        <option value="">— not used</option>
                        {fields.map((x) => (
                          <option value={x.name} key={x.name}>{x.name}{x.required ? ' *' : ''}</option>
                        ))}
                      </select>
                    </td>
                    <td className="absorber-map-type mono">{f ? `${f.type || 'string'}${f.required ? ' · required' : ''}` : ''}</td>
                    <td className={becomes.klass}>{becomes.node}</td>
                  </tr>
                );
              })}
              {/* A required field with nothing pointing at it is a row of this
                  table too — the hole is the reason the button below is dead. */}
              {missing.map((name) => (
                <tr className="absorber-map-missing" key={name}>
                  <td className="absorber-map-nothing">— nothing —</td>
                  <td className="absorber-map-arrow">→</td>
                  <td className="mono absorber-map-missing-field">{name} *</td>
                  <td className="absorber-map-type mono">{typeOf(name)} · required</td>
                  <td className="absorber-map-bad">nothing maps here — every row rejected</td>
                </tr>
              ))}
            </tbody>
          </table>

          {unmapped.length ? (
            <div className="absorber-map-note">
              {unmapped.length} column{unmapped.length === 1 ? '' : 's'} not used:{' '}
              {unmapped.map((u, i) => <React.Fragment key={u}>{i ? ', ' : ''}<span className="mono">{u}</span></React.Fragment>)}
            </div>
          ) : null}

          <div className="absorber-map-samplehead">first {sample.length} rows, exactly as they would be written</div>
          {sample.map((n, i) => (
            <div className="absorber-map-sample" key={i}>
              <div className="absorber-map-sample-title">{n.title}</div>
              <div className="absorber-map-sample-json mono">{JSON.stringify(n.value_json)}</div>
            </div>
          ))}
        </>
      ) : schemaName
        ? <div className="absorber-schema-unavailable">could not read that schema’s shape — hand it to the absorber instead</div>
        : <div className="absorber-schema-unavailable">pick what these rows are, above — or let the absorber read the file and say</div>}

      <div className="absorber-map-choice">
        <div className="absorber-map-option">
          <button type="button" className="surface-process-btn" disabled={!!missing.length || !fields.length || writing}
                  onClick={write}>
            {schemaName
              ? `add ${rowsLabel} ${schemaName}${rows.length === 1 ? '' : 's'} to your graph`
              : 'add these rows to your graph'}
          </button>
          <div className="absorber-map-option-why">
            {missing.length
              ? <>{missing.map((m, i) => <React.Fragment key={m}>{i ? ', ' : ''}<span className="mono">{m}</span></React.Fragment>)} {missing.length === 1 ? 'has' : 'have'} no column — every row would be rejected, so there is nothing to add yet.</>
              : 'every row, exactly as shown above. no interpretation, nothing else read.'}
          </div>
        </div>
      </div>
      {badTypes.length ? (
        <div className="absorber-map-typewarn">
          {badTypes.map((b, i) => (
            <div key={i}>
              <span className="mono">{b.column}</span> holds values{' '}
              <span className="mono">{b.field}</span> cannot take — it is declared{' '}
              {b.type}, and {b.rows} row{b.rows === 1 ? '' : 's'} read like{' '}
              <span className="mono">{b.example}</span>. Those rows will be rejected.
            </div>
          ))}
        </div>
      ) : null}
      <div className="absorber-map-status">{status}</div>
    </div>
  );
}

// ── History sub-view ───────────────────────────────────────────────────────
function HistoryList({ onBack, onOpen }) {
  const history = getHistory();
  return (
    <div className="absorber-wrap">
      <div className="absorber-history">
        <div className="absorber-history-header">
          <button type="button" className="absorber-back-btn" onClick={onBack}>← absorber</button>
          <span className="absorber-history-title">past sessions</span>
        </div>
        <div className="absorber-history-list">
          {history.length ? history.map((s) => {
            const date = s.started_at ? new Date(s.started_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';
            const time = s.started_at ? new Date(s.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
            const fileNames = (s.files || []).join(', ') || 'untitled';
            return (
              <div className="absorber-history-item" key={s.id} onClick={() => onOpen(s)}>
                <div className="absorber-history-files">{fileNames}</div>
                <div className="absorber-history-meta">{date} {time} · {s.proposal_count || 0} proposals</div>
              </div>
            );
          }) : <div className="absorber-empty">no past sessions</div>}
        </div>
      </div>
    </div>
  );
}
